import React from 'react';
import { Pressable, View } from 'react-native';

import { SvgIcon, Text, WebView } from '@/components/ui';
import Modal from '@/components/ui/modal/modal';
import { hitSlop } from '@/constants';
import { STRINGS } from '@/constants/strings';
import {
  LEGAL_DOCUMENT_API_TYPE,
  LEGAL_DOCUMENTS,
  type LegalDocumentType,
} from '@/features/auth/components/legal';
import { buildLegalDocumentHtml } from '@/features/auth/components/legal/legal-document-html';
import { createStyles, useScaling } from '@/hooks';
import { useLegalDocumentQuery } from '@/queries/use-legal-documents-query';
import { colors } from '@/theme/colors';

type AuthLegalPopupProps = {
  visible: boolean;
  /** Which document to render. `null` keeps the popup mounted-but-hidden during the exit animation. */
  document: LegalDocumentType | null;
  onClose: () => void;
};

/**
 * Centered popup that renders the Terms of Service / Privacy Policy as HTML in a
 * WebView, matching the Guardian "Privacy Policy" overlay in Figma: a white
 * rounded card with a title, a close button, and scrollable legal copy.
 */
export function AuthLegalPopup({ visible, document, onClose }: AuthLegalPopupProps) {
  const styles = useStyles();
  const { isTablet } = useScaling();

  // Fall back to `privacy` so the WebView keeps its last content while the modal animates out.
  const key = document ?? 'privacy';
  // Fetched on every screen, signed in or not: the request never alerts on failure and the
  // bundled document covers it, so a pre-auth 401 costs nothing.
  const { data: fetched } = useLegalDocumentQuery(LEGAL_DOCUMENT_API_TYPE[key]);

  // Live copy when the request has landed, the bundled document until then (and if it fails).
  const doc = fetched
    ? { title: fetched.title, html: buildLegalDocumentHtml(fetched.content) }
    : LEGAL_DOCUMENTS[key];

  return (
    <Modal
      visible={visible}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      contentContainerStyle={styles.center}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Text variant="Font Guardian App/Heading/Heading H5 24px" style={styles.title}>
            {doc.title}
          </Text>
          <Pressable
            onPress={onClose}
            hitSlop={hitSlop}
            accessibilityRole="button"
            accessibilityLabel={STRINGS.auth.legal.closeAccessibilityLabel}>
            <SvgIcon name="IcClose" size={14} color={'#64748B'} />
          </Pressable>
        </View>

        <WebView
          source={{ html: doc.html }}
          originWhitelist={['*']}
          style={styles.webview}
          containerStyle={styles.webview}
          showsVerticalScrollIndicator
          forceMobileOnTablet={false}
          isTablet={isTablet}
          // Avoid the brief white flash before the (transparent) HTML paints.
          androidLayerType="hardware"
        />
      </View>
    </Modal>
  );
}

const useStyles = createStyles(({ scale, deviceHeight }) => ({
  center: {},
  card: {
    width: scale({ phone: 335, tablet: 978 }),
    // Figma card is 720 tall on an 812 screen; cap to the viewport on shorter devices.
    height: Math.min(scale(720), deviceHeight * 0.85),
    backgroundColor: colors['0'],
    borderRadius: 16,
    paddingLeft: scale(12),
    paddingRight: scale(16),
    paddingVertical: scale(16),
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    flex: 1,
    color: colors['10'],
  },
  webview: {
    flex: 1,
    marginTop: scale(6),
    backgroundColor: 'transparent',
  },
}));

export default AuthLegalPopup;
